import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowUp, Facebook, Instagram, ChevronRight, ShieldCheck } from 'lucide-react';
import { useContactSettings } from '../../hooks/useContactSettings';
import logo from '../../assets/favicon.png';

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M16.6 5.82c-.9-.98-1.4-2.25-1.4-3.57h-3.15v13.3a2.7 2.7 0 11-2.7-2.7c.24 0 .48.03.7.09V9.79a5.9 5.9 0 00-.7-.04A5.85 5.85 0 003 15.6a5.85 5.85 0 0011.7 0V8.83a8.5 8.5 0 004.9 1.56V7.24a5.5 5.5 0 01-3-1.42z" />
  </svg>
);

const FooterLink = ({ to, children }) => (
  <li>
    <Link to={to} className="flex items-center gap-1.5 text-gray-600 hover:text-red-600 hover:translate-x-0.5 transition-all">
      <ChevronRight className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
      {children}
    </Link>
  </li>
);

const ColumnHeading = ({ children }) => (
  <div className="mb-3">
    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">{children}</h3>
    <div className="w-6 h-0.5 bg-red-600 rounded mt-1.5" />
  </div>
);

const Footer = () => {
  const { contactSettings } = useContactSettings();

  return (
    <footer className="bg-white text-gray-700 pt-6 md:pt-10 pb-20 md:pb-6 border-t border-gray-200">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        {/* Main footer sections */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-x-4 gap-y-6 md:gap-6 mb-6 md:mb-8">

          {/* Company Info */}
          <div className="col-span-2 lg:col-span-1 space-y-3">
            <div className="flex items-center">
              <img src={logo} alt="CureHive Nepal" className="h-8 md:h-10 w-auto object-contain mr-3" />
              <span className="text-lg md:text-xl font-extrabold tracking-tight">
                <span className="text-gray-900">CureHive</span> <span className="text-red-600">Nepal</span>
              </span>
            </div>
            <p className="text-xs md:text-sm text-gray-500 leading-relaxed max-w-sm">
              Your trusted destination for surgical, dental, and skincare products.
              Quality you can trust, care you can rely on.
            </p>
            <div className="space-y-2">
              <a href={`mailto:${contactSettings?.email || 'curehivenepal@gmail.com'}`} className="flex items-center gap-2.5 text-xs md:text-sm text-gray-600 hover:text-red-600 transition-colors group">
                <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-red-50 text-red-600 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                  <Mail className="w-3.5 h-3.5" />
                </span>
                {contactSettings?.email || 'curehivenepal@gmail.com'}
              </a>
              <a href={`tel:${contactSettings?.phone || '+977-1-4000000'}`} className="flex items-center gap-2.5 text-xs md:text-sm text-gray-600 hover:text-red-600 transition-colors group">
                <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-red-50 text-red-600 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                  <Phone className="w-3.5 h-3.5" />
                </span>
                {contactSettings?.phone || '+977-1-4000000'}
              </a>
              <div className="flex items-center gap-2.5 text-xs md:text-sm text-gray-600">
                <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
                  <MapPin className="w-3.5 h-3.5" />
                </span>
                <span className="leading-relaxed">
                  {contactSettings?.streetAddress || 'Kathmandu'}, {contactSettings?.city || 'Bagmati'}, {contactSettings?.province || 'Nepal'}
                </span>
              </div>
            </div>
          </div>

          {/* Shop */}
          <div className="col-span-1">
            <ColumnHeading>Shop</ColumnHeading>
            <ul className="space-y-2 text-sm">
              <FooterLink to="/products">All Products</FooterLink>
              <FooterLink to="/categories">Categories</FooterLink>
              <FooterLink to="/brands">Brands</FooterLink>
              <FooterLink to="/best-sellers">Best Sellers</FooterLink>
              <FooterLink to="/sales">Offers &amp; Deals</FooterLink>
              <FooterLink to="/near-expiry">Near Expiry</FooterLink>
            </ul>
          </div>

          {/* Company */}
          <div className="col-span-1">
            <ColumnHeading>Company</ColumnHeading>
            <ul className="space-y-2 text-sm">
              <FooterLink to="/about">About Us</FooterLink>
              <FooterLink to="/about#who-we-are">Our Story</FooterLink>
              <FooterLink to="/about#why-choose-us">Why Choose Us</FooterLink>
              <FooterLink to="/events">Events</FooterLink>
            </ul>
          </div>

          {/* Support */}
          <div className="col-span-1">
            <ColumnHeading>Support</ColumnHeading>
            <ul className="space-y-2 text-sm">
              <FooterLink to="/login">My Account</FooterLink>
              <FooterLink to="/account/orders">Track Order</FooterLink>
              <FooterLink to="/account/wishlist">Wishlist</FooterLink>
              <FooterLink to="/privacy">Privacy Policy</FooterLink>
            </ul>
          </div>

          {/* Newsletter & Socials */}
          <div className="col-span-1 min-w-0">
            <ColumnHeading>Stay Connected</ColumnHeading>
            <p className="text-xs md:text-sm text-gray-500 mb-2.5 leading-relaxed">
              Subscribe to receive updates, exclusive deals, and healthcare insights.
            </p>
            <form className="mb-4">
              <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden focus-within:border-red-400 focus-within:ring-1 focus-within:ring-red-400 transition-all bg-white">
                <Mail className="w-4 h-4 text-gray-400 ml-3 flex-shrink-0" />
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="px-2 py-2 md:py-2.5 w-full text-gray-900 placeholder-gray-400 text-xs md:text-sm focus:outline-none"
                  required
                />
                <button
                  type="submit"
                  className="bg-red-600 hover:bg-red-700 text-white px-4 md:px-5 py-2 md:py-2.5 text-xs md:text-sm font-semibold transition-colors flex-shrink-0"
                >
                  Subscribe
                </button>
              </div>
            </form>
            <div className="flex flex-wrap items-start gap-3 sm:gap-4">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 text-gray-600 hover:text-red-600 transition-colors group">
                <span className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                  <Facebook className="w-4 h-4" />
                </span>
                <span className="text-xs">Facebook</span>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 text-gray-600 hover:text-red-600 transition-colors group">
                <span className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                  <Instagram className="w-4 h-4" />
                </span>
                <span className="text-xs">Instagram</span>
              </a>
              <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 text-gray-600 hover:text-red-600 transition-colors group">
                <span className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                  <TikTokIcon className="w-4 h-4" />
                </span>
                <span className="text-xs">TikTok</span>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-200 pt-4 md:pt-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 text-xs md:text-sm text-gray-500 text-center md:text-left">
            <ShieldCheck className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span>Trusted by thousands of customers and healthcare professionals across Nepal.</span>
          </div>
          <p className="text-xs md:text-sm text-gray-500 order-first md:order-none">
            &copy; {new Date().getFullYear()} <span className="text-red-600 font-medium">CureHive Nepal</span>. All rights reserved.
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2 text-xs md:text-sm text-red-600 hover:text-red-700 transition-colors flex-shrink-0"
          >
            Back to top
            <span className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center">
              <ArrowUp className="w-3.5 h-3.5" />
            </span>
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
